"use client"

import { cn } from "@rarible_int/anaconda_ui_components"

interface CollectorItem {
  id: string
  title: string
  description?: string
  image?: string // Main display image (avatar)
  itemImage?: string // New optional field for additional item image
  itemUrl?: string // New optional field for item URL/link
  collectorUrl?: string // New optional field for collector profile URL/link
  metadata?: Record<string, unknown> // For flexible data like numbers, stats, etc.
}

interface CollectorsSectionProps {
  title: string
  description?: string
  columns?: 2 | 3 | 4 | 6
  items: CollectorItem[]
  ctaText?: string // Optional call-to-action text for the section
  ctaLink?: string // Optional call-to-action link for the section
}

interface CollectorsSectionComponentProps {
  props: CollectorsSectionProps
  styles?: {
    backgroundColor?: string
    textColor?: string
    padding?: string
    margin?: string
  }
}

export function CollectorsSection({
  props,
  styles,
}: CollectorsSectionComponentProps) {
  const { title, description, columns = 6, items, ctaText, ctaLink } = props

  return (
    <div
      style={{
        color: styles?.textColor,
        padding: styles?.padding,
        margin: styles?.margin,
      }}
    >
      {/* Header */}
      <div className="mb-6 text-left sm:mb-8 md:mb-10 lg:mb-12">
        <h3 className="mb-2 font-bold text-xl md:mb-3 md:text-2xl lg:text-3xl">
          {title}
        </h3>
        {description && (
          <p className="text-sm opacity-70 sm:text-base md:text-lg lg:text-xl">
            {description}
          </p>
        )}
      </div>

      {/* Collectors Grid */}
      <CollectorsLayout items={items} columns={columns} />

      {/* CTA Button */}
      {ctaText && ctaLink && (
        <div className="mt-8 flex justify-center sm:mt-10 md:mt-12 lg:mt-16">
          <a
            href={ctaLink}
            target="_blank"
            rel="noopener noreferrer"
            className="flex flex-col items-center justify-center gap-1 rounded-full border-[#F73FF3] border-[3px] bg-transparent px-6 py-3 text-center font-normal text-base text-white leading-6 transition-all hover:scale-105 active:scale-95 sm:px-8 sm:py-4 sm:text-lg md:text-xl"
            style={{
              fontFamily: '"Faro Display Variable Trial", sans-serif',
            }}
          >
            {ctaText}
          </a>
        </div>
      )}

      {/* Empty State */}
      {items.length === 0 && (
        <div className="py-12 text-center opacity-50">
          <p>No collectors to display</p>
        </div>
      )}
    </div>
  )
}

function CollectorsLayout({
  items,
  columns,
}: {
  items: CollectorItem[]
  columns: 2 | 3 | 4 | 6
}) {
  const getResponsiveLayout = (): {
    gridCols: string
    gap: string
    imageSize: "small" | "medium" | "default"
  } => {
    const itemCount = items.length

    // For very few items (1-2), use larger spacing and medium images
    if (itemCount <= 2) {
      return {
        gridCols: "grid-cols-1 xs:grid-cols-1 sm:grid-cols-2",
        gap: "gap-[84px] xs:gap-[90px] sm:gap-[100px] md:gap-[120px] lg:gap-[140px]",
        imageSize: "medium" as const,
      }
    }

    // For 3-4 items, moderate spacing with medium images
    if (itemCount <= 4) {
      return {
        gridCols:
          "grid-cols-1 xs:grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4",
        gap: "gap-[84px] xs:gap-[80px] sm:gap-[96px] md:gap-[108px] lg:gap-[120px]",
        imageSize: "medium" as const,
      }
    }

    // For 5+ items, use the specified columns with adaptive layout
    const getGridColumns = () => {
      switch (columns) {
        case 2:
          return "grid-cols-1 xs:grid-cols-2 sm:grid-cols-2"
        case 3:
          return "grid-cols-1 xs:grid-cols-2 sm:grid-cols-3 lg:grid-cols-3"
        case 4:
          return "grid-cols-1 xs:grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-4"
        case 6:
          return "grid-cols-1 xs:grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6"
        default:
          return "grid-cols-1 xs:grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6"
      }
    }

    return {
      gridCols: getGridColumns(),
      gap: "gap-[84px] xs:gap-[60px] sm:gap-[70px] md:gap-[80px] lg:gap-[84px]",
      imageSize: "default" as const,
    }
  }

  const layout = getResponsiveLayout()

  return (
    <div className={`grid ${layout.gridCols} ${layout.gap}`}>
      {items.map((item) => (
        <CollectorCard key={item.id} item={item} imageSize={layout.imageSize} />
      ))}
    </div>
  )
}

function CollectorCard({
  item,
  imageSize = "default",
}: {
  item: CollectorItem
  imageSize?: "small" | "medium" | "default"
}) {
  // Get responsive sizing classes based on imageSize prop
  const getSizeClasses = () => {
    switch (imageSize) {
      case "small":
        return {
          container:
            "w-full max-w-[160px] mx-auto sm:max-w-[140px] md:max-w-[160px]",
          spacing: "mb-3 sm:mb-4 md:mb-5",
          title: "text-sm sm:text-base md:text-lg font-medium",
          description: "text-xs sm:text-sm",
          overlay: "h-16 w-16 sm:h-14 sm:w-14 md:h-16 md:w-16",
        }
      case "medium":
        return {
          container:
            "w-full max-w-[200px] mx-auto sm:max-w-[180px] md:max-w-[200px]",
          spacing: "mb-3 sm:mb-4 md:mb-5",
          title: "text-base sm:text-lg md:text-xl font-medium",
          description: "text-sm sm:text-base",
          overlay: "h-18 w-18 sm:h-16 sm:w-16 md:h-20 md:w-20",
        }
      default:
        return {
          container: "w-full",
          spacing: "mb-2 sm:mb-3 md:mb-4",
          title: "text-sm sm:text-base md:text-lg font-medium",
          description: "text-xs sm:text-sm md:text-base",
          overlay: "h-16 w-16 sm:h-14 sm:w-14 md:h-16 md:w-16",
        }
    }
  }

  const sizeClasses = getSizeClasses()

  const collectorContent = (
    <div className={`group text-center ${sizeClasses.container}`}>
      <div className={`relative ${sizeClasses.spacing}`}>
        {/* Main collector avatar */}
        <div className="aspect-square w-full overflow-hidden rounded-full">
          {item.image ? (
            <img
              src={item.image}
              alt={item.title}
              className="h-full w-full object-cover transition-transform group-hover:scale-110"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-gray-700">
              <span className="font-bold text-2xl text-gray-400">
                {item.title.charAt(0).toUpperCase()}
              </span>
            </div>
          )}
        </div>

        {/* Item image overlay - positioned in bottom right */}
        {item.itemImage && (
          <div
            className={cn(
              "-bottom-1 -right-1 xs:-bottom-2 xs:-right-2 absolute shadow-xl",
              sizeClasses.overlay,
            )}
          >
            {item.itemUrl ? (
              <a
                href={item.itemUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="block h-full w-full"
                onClick={(e) => e.stopPropagation()}
              >
                <img
                  src={item.itemImage}
                  alt={`${item.title} item`}
                  className="h-full w-full object-contain transition-transform hover:scale-110"
                />
              </a>
            ) : (
              <img
                src={item.itemImage}
                alt={`${item.title} item`}
                className="h-full w-full object-contain"
              />
            )}
          </div>
        )}

        {/* Special badge for featured collectors */}
        {item.metadata?.badge != null && (
          <div className="-top-2 -right-2 absolute flex h-8 w-8 items-center justify-center rounded-full bg-blue-500 font-bold text-white text-xs shadow-lg">
            {String(item.metadata.badge)}
          </div>
        )}
      </div>

      <div>
        <h4 className={`mb-1 ${sizeClasses.title} text-foreground`}>
          {item.title}
        </h4>
        {item.description && item.description.trim() !== "" && (
          <p className={sizeClasses.description} style={{ color: "#F73FF3" }}>
            {item.description}
          </p>
        )}
      </div>
    </div>
  )

  return item.collectorUrl ? (
    <a
      href={item.collectorUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="block cursor-pointer"
    >
      {collectorContent}
    </a>
  ) : (
    collectorContent
  )
}
